/**
 * "Abstract Floorplans" section for the plan template drawer.
 * Summarizes the template's floorplans and opens the full-screen editor.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { HiMap } from 'react-icons/hi2';
import { usePlanFloorplans } from '../../../hooks/queries/useReunification';
import { FloorplanEditor } from './FloorplanEditor';

interface FloorplansSectionProps {
  templateId: string;
}

export function FloorplansSection({ templateId }: FloorplansSectionProps) {
  const { t } = useTranslation();
  const [editorOpen, setEditorOpen] = useState(false);
  const { data: floorplans, isLoading } = usePlanFloorplans(templateId);

  return (
    <VStack align="stretch" gap={3} data-e2e="floorplans-section">
      <HStack justify="space-between">
        <Text fontWeight={600}>{t('reunification.abstractFloorplans', 'Abstract Floorplans')}</Text>
        <ButtonComponent
          visual="secondary"
          size="sm"
          onClick={() => setEditorOpen(true)}
          data-e2e="open-floorplan-editor"
        >
          <HiMap /> {t('reunification.openEditor', 'Open Editor')}
        </ButtonComponent>
      </HStack>
      {isLoading ? (
        <Spinner size="sm" />
      ) : !floorplans?.length ? (
        <Text fontSize="sm" color="text.secondary">
          {t(
            'reunification.noFloorplansDescription',
            'No floorplans yet. The editor lets you lay out stations, zones, and movement flows for this plan.',
          )}
        </Text>
      ) : (
        <VStack align="stretch" gap={1}>
          {floorplans.map((fp) => (
            <HStack
              key={fp.public_id}
              justify="space-between"
              px={3}
              py={2}
              bg="bg.subtle"
              borderRadius="md"
            >
              <Text fontSize="sm">{fp.name}</Text>
              <HStack gap={3}>
                <Text fontSize="xs" color="text.secondary">
                  {t('reunification.stationsCount', '{{count}} stations', { count: fp.station_count })}
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  {t('reunification.zonesCount', '{{count}} zones', { count: fp.zone_count })}
                </Text>
              </HStack>
            </HStack>
          ))}
        </VStack>
      )}

      <Dialog.Root
        open={editorOpen}
        onOpenChange={(d) => setEditorOpen(d.open)}
        size="cover"
        closeOnInteractOutside={false}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content display="flex" flexDirection="column">
              <Dialog.Header py={3}>
                <Dialog.Title>{t('reunification.floorplanEditor', 'Floorplan Editor')}</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    data-e2e="floorplan-editor-close"
                  >
                    {t('common.close', 'Close')}
                  </ButtonComponent>
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body
                p={0}
                flex="1"
                minH={0}
                display="flex"
                flexDirection="column"
              >
                {editorOpen && <FloorplanEditor templateId={templateId} />}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </VStack>
  );
}
